import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { PRICING, FAQS } from '../mock';
import { WHATSAPP_URL } from '../lib/contact';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Button } from './ui/button';

export const Pricing = () => (
  <section id="pricing" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">PLANOS</div>
        <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a0e27]">
          Escolha o plano ideal para sua operação
        </h2>
        <p className="mt-4 text-slate-600 text-lg">
          Sem taxa de setup. Cancele quando quiser. 14 dias de trial em todos os planos.
        </p>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {PRICING.map((p) => (
          <div
            key={p.name}
            className={`relative rounded-3xl p-8 border transition-all hover:-translate-y-1 ${
              p.highlighted
                ? 'bg-[#0a0e27] text-white border-[#0a0e27] shadow-2xl'
                : 'bg-white border-slate-200 hover:shadow-xl'
            }`}
          >
            {p.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF5B24] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                Mais Popular
              </span>
            )}
            <h3 className={`text-lg font-extrabold ${p.highlighted ? 'text-white' : 'text-[#0a0e27]'}`}>{p.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              {p.price === 'Custom' ? (
                <span className={`text-4xl font-black ${p.highlighted ? 'text-white' : 'text-[#0a0e27]'}`}>Custom</span>
              ) : (
                <>
                  <span className={`text-2xl font-bold ${p.highlighted ? 'text-white/70' : 'text-slate-500'}`}>R$</span>
                  <span className={`text-5xl font-black ${p.highlighted ? 'text-white' : 'text-[#0a0e27]'}`}>{p.price}</span>
                </>
              )}
            </div>
            <div className={`text-xs mt-1 ${p.highlighted ? 'text-white/60' : 'text-slate-500'}`}>{p.period}</div>
            <p className={`mt-3 text-sm ${p.highlighted ? 'text-white/80' : 'text-slate-600'}`}>{p.description}</p>
            <div className={`my-6 h-px ${p.highlighted ? 'bg-white/10' : 'bg-slate-100'}`} />
            <ul className="space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className={p.highlighted ? 'text-[#FF5B24] mt-0.5 shrink-0' : 'text-[#FF5B24] mt-0.5 shrink-0'} />
                  <span className={p.highlighted ? 'text-white/90' : 'text-slate-700'}>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className={`mt-8 w-full h-11 rounded-full text-sm font-semibold ${
                p.highlighted ? 'btn-coral' : 'bg-[#0a0e27] text-white hover:bg-[#1a1f4d]'
              }`}
            >
              <a
                href={`https://wa.me/5511974512072?text=${encodeURIComponent(
                  `Olá! Tenho interesse no plano ${p.name} do CoreDesk.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {p.price === 'Custom' ? 'Falar com vendas' : 'Começar agora'}
              </a>
            </Button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FAQ = () => (
  <section id="faq" className="py-24 bg-gradient-to-b from-white to-[#FAF6F2]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">DÚVIDAS</div>
        <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a0e27]">Perguntas frequentes</h2>
        <p className="mt-4 text-slate-600">Respondemos aqui as dúvidas mais frequentes, com total transparência.</p>
      </div>
      <div className="mt-12">
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white rounded-2xl border border-slate-100 px-5 data-[state=open]:shadow-lg"
            >
              <AccordionTrigger className="text-left font-bold text-[#0a0e27] hover:no-underline py-5">
                <span className="flex items-center gap-4">
                  <span className="text-[#FF5B24] font-black text-sm w-6">{String(i + 1).padStart(2, '0')}</span>
                  <span>{f.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pl-10 pb-5">
                {f.a.split('\n\n').map((para, pi) => (
                  <p key={pi} className={pi > 0 ? 'mt-3' : ''}>
                    {para.split(/(https?:\/\/[^\s]+)/g).map((chunk, ci) =>
                      /^https?:\/\//.test(chunk) ? (
                        <a
                          key={ci}
                          href={chunk}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF5B24] font-semibold hover:underline break-all"
                        >
                          {chunk}
                        </a>
                      ) : (
                        <React.Fragment key={ci}>{chunk}</React.Fragment>
                      )
                    )}
                  </p>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="relative py-24 overflow-hidden bg-[#0a0e27]">
    <div className="absolute inset-0 bg-grid opacity-[0.07]" />
    <div className="absolute -top-24 -left-24 w-[500px] h-[500px] bg-[#FF5B24]/25 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-3xl" />
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 border border-white/20 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FF5B24] flex items-center justify-center">
          <span className="text-white font-black text-lg">C</span>
        </div>
      </div>
      <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05]">
        Estamos sempre <br /> <span className="text-[#FF5B24]">prontos para ajudar você.</span>
      </h2>
      <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
        Muito além do atendimento: hub estratégico com IA para sustentar crescimento, eficiência e evolução contínua.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button asChild className="btn-coral rounded-full h-12 px-6 text-sm font-semibold gap-1.5">
          <Link to="/contato">Agendar demonstração <ArrowUpRight size={16} /></Link>
        </Button>
        <Button asChild variant="outline" className="rounded-full h-12 px-6 text-sm font-semibold bg-transparent border-white/25 text-white hover:bg-white hover:text-[#0a0e27]">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">Falar no WhatsApp</a>
        </Button>
      </div>
    </div>
  </section>
);
